// src/services/productionStorage.ts
//
// Phase 2D-lite storage abstraction.
//
//   ProductionStorageProvider  ← interface every backend implements
//   LocalStorageProvider       ← Phase 1+2 default, fully working
//   AdvantageProvider          ← stub — Phase 3 will wire to agent recipes
//   DuckDbProvider             ← stub — Phase 3 will wire to local DuckDB WASM
//
// The functional surface (getItems / saveItem / ...) stays the same as Phase 1
// so pages don't change. The active provider per collection comes from a
// per-tenant config in localStorage (Settings → Source picker).

import type {
  ProductionItem,
  ProductionRecipe,
  ProductionProduct,
  ForecastLine,
  ProductionStorageBackend,
  ProductionStorageConfig,
  ProductionExport,
} from '../types/production';
import { DEFAULT_STORAGE_CONFIG } from '../types/production';
import { SEED_ITEMS, SEED_RECIPES, SEED_PRODUCTS } from './productionSeedData';

// ─── Helpers ──────────────────────────────────────────────────────────────

function nowIso(): string {
  return new Date().toISOString();
}

function generateId(prefix: string): string {
  const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(16).slice(2) + Math.random().toString(16).slice(2);
  return `${prefix}-${uuid.replace(/-/g, '').slice(0, 8)}`;
}

// ─── Provider interface ───────────────────────────────────────────────────

export interface ProductionStorageProvider {
  readonly backend: ProductionStorageBackend;
  readonly writable: boolean;

  getItems(slug: string): ProductionItem[];
  saveItem(slug: string, item: Partial<ProductionItem> & { name: string }): ProductionItem;
  deleteItem(slug: string, id: string): void;

  getRecipes(slug: string): ProductionRecipe[];
  saveRecipe(slug: string, recipe: Partial<ProductionRecipe> & { name: string }): ProductionRecipe;
  deleteRecipe(slug: string, id: string): void;

  getProducts(slug: string): ProductionProduct[];
  saveProduct(slug: string, product: Partial<ProductionProduct> & { name: string; recipeId: string }): ProductionProduct;
  deleteProduct(slug: string, id: string): void;

  getForecast(slug: string): ForecastLine[];
  saveForecastLine(slug: string, line: Partial<ForecastLine> & { productId: string; qty: number }): ForecastLine;
  deleteForecastLine(slug: string, id: string): void;
  clearForecast(slug: string): void;
}

// ─── LocalStorageProvider ─────────────────────────────────────────────────

const LS_PREFIX = 'production_';

function lsKey(slug: string, collection: string): string {
  return `${LS_PREFIX}${slug}_${collection}`;
}

function lsRead<T>(slug: string, collection: string): T[] {
  if (!slug) return [];
  try {
    const raw = localStorage.getItem(lsKey(slug, collection));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function lsWrite<T>(slug: string, collection: string, data: T[]): void {
  if (!slug) return;
  localStorage.setItem(lsKey(slug, collection), JSON.stringify(data));
}

class LocalStorageProviderImpl implements ProductionStorageProvider {
  readonly backend: ProductionStorageBackend = 'local';
  readonly writable = true;

  getItems(slug: string): ProductionItem[] {
    return lsRead<ProductionItem>(slug, 'items').sort((a, b) => a.name.localeCompare(b.name));
  }

  saveItem(slug: string, item: Partial<ProductionItem> & { name: string }): ProductionItem {
    const all = lsRead<ProductionItem>(slug, 'items');
    const now = nowIso();
    if (item.id) {
      const idx = all.findIndex((x) => x.id === item.id);
      if (idx >= 0) {
        const merged: ProductionItem = { ...all[idx], ...item, id: all[idx].id, updatedAt: now };
        all[idx] = merged;
        lsWrite(slug, 'items', all);
        return merged;
      }
    }
    const created: ProductionItem = {
      id: item.id || generateId('item'),
      name: item.name,
      description: item.description ?? '',
      unit: item.unit ?? 'lb',
      costPerUnit: item.costPerUnit ?? 0,
      category: item.category,
      onHand: item.onHand,
      createdAt: now,
      updatedAt: now,
    };
    all.push(created);
    lsWrite(slug, 'items', all);
    return created;
  }

  deleteItem(slug: string, id: string): void {
    const all = lsRead<ProductionItem>(slug, 'items').filter((x) => x.id !== id);
    lsWrite(slug, 'items', all);
  }

  getRecipes(slug: string): ProductionRecipe[] {
    return lsRead<ProductionRecipe>(slug, 'recipes').sort((a, b) => a.name.localeCompare(b.name));
  }

  saveRecipe(slug: string, recipe: Partial<ProductionRecipe> & { name: string }): ProductionRecipe {
    const all = lsRead<ProductionRecipe>(slug, 'recipes');
    const now = nowIso();
    if (recipe.id) {
      const idx = all.findIndex((x) => x.id === recipe.id);
      if (idx >= 0) {
        const merged: ProductionRecipe = {
          ...all[idx],
          ...recipe,
          id: all[idx].id,
          components: recipe.components ?? all[idx].components,
          updatedAt: now,
        };
        all[idx] = merged;
        lsWrite(slug, 'recipes', all);
        return merged;
      }
    }
    const created: ProductionRecipe = {
      id: recipe.id || generateId('recipe'),
      name: recipe.name,
      description: recipe.description ?? '',
      category: recipe.category,
      yieldQty: recipe.yieldQty ?? 1,
      yieldUnit: recipe.yieldUnit ?? 'lb',
      components: recipe.components ?? [],
      sellPrice: recipe.sellPrice,
      notes: recipe.notes ?? '',
      createdAt: now,
      updatedAt: now,
    };
    all.push(created);
    lsWrite(slug, 'recipes', all);
    return created;
  }

  deleteRecipe(slug: string, id: string): void {
    const all = lsRead<ProductionRecipe>(slug, 'recipes').filter((x) => x.id !== id);
    lsWrite(slug, 'recipes', all);
  }

  getProducts(slug: string): ProductionProduct[] {
    return lsRead<ProductionProduct>(slug, 'products').sort((a, b) => a.name.localeCompare(b.name));
  }

  saveProduct(
    slug: string,
    product: Partial<ProductionProduct> & { name: string; recipeId: string },
  ): ProductionProduct {
    const all = lsRead<ProductionProduct>(slug, 'products');
    const now = nowIso();
    if (product.id) {
      const idx = all.findIndex((x) => x.id === product.id);
      if (idx >= 0) {
        const merged: ProductionProduct = {
          ...all[idx],
          ...product,
          id: all[idx].id,
          packaging: product.packaging ?? all[idx].packaging,
          updatedAt: now,
        };
        all[idx] = merged;
        lsWrite(slug, 'products', all);
        return merged;
      }
    }
    const created: ProductionProduct = {
      id: product.id || generateId('product'),
      name: product.name,
      sku: product.sku,
      description: product.description,
      category: product.category,
      recipeId: product.recipeId,
      unitsPerBatch: product.unitsPerBatch ?? 1,
      packaging: product.packaging ?? [],
      sellPrice: product.sellPrice,
      createdAt: now,
      updatedAt: now,
    };
    all.push(created);
    lsWrite(slug, 'products', all);
    return created;
  }

  deleteProduct(slug: string, id: string): void {
    const all = lsRead<ProductionProduct>(slug, 'products').filter((x) => x.id !== id);
    lsWrite(slug, 'products', all);
  }

  getForecast(slug: string): ForecastLine[] {
    return lsRead<ForecastLine>(slug, 'forecast');
  }

  saveForecastLine(
    slug: string,
    line: Partial<ForecastLine> & { productId: string; qty: number },
  ): ForecastLine {
    const all = lsRead<ForecastLine>(slug, 'forecast');
    if (line.id) {
      const idx = all.findIndex((x) => x.id === line.id);
      if (idx >= 0) {
        const merged: ForecastLine = { ...all[idx], ...line, id: all[idx].id };
        all[idx] = merged;
        lsWrite(slug, 'forecast', all);
        return merged;
      }
    }
    const created: ForecastLine = {
      id: line.id || generateId('fcst'),
      productId: line.productId,
      qty: line.qty,
      targetDate: line.targetDate,
      notes: line.notes,
    };
    all.push(created);
    lsWrite(slug, 'forecast', all);
    return created;
  }

  deleteForecastLine(slug: string, id: string): void {
    const all = lsRead<ForecastLine>(slug, 'forecast').filter((x) => x.id !== id);
    lsWrite(slug, 'forecast', all);
  }

  clearForecast(slug: string): void {
    lsWrite(slug, 'forecast', []);
  }
}

// ─── Stub providers (Phase 3) ─────────────────────────────────────────────

class NotImplementedProvider implements ProductionStorageProvider {
  readonly backend: ProductionStorageBackend;
  readonly writable: boolean;
  private readonly featureName: string;

  constructor(backend: ProductionStorageBackend, featureName: string) {
    this.backend = backend;
    this.writable = false;
    this.featureName = featureName;
  }

  private err(op: string): never {
    throw new Error(`${this.featureName} backend is not implemented yet (op: ${op}). Switch to Local in Settings.`);
  }

  getItems(): ProductionItem[] { return []; }
  saveItem(): ProductionItem { this.err('saveItem'); }
  deleteItem(): void { this.err('deleteItem'); }
  getRecipes(): ProductionRecipe[] { return []; }
  saveRecipe(): ProductionRecipe { this.err('saveRecipe'); }
  deleteRecipe(): void { this.err('deleteRecipe'); }
  getProducts(): ProductionProduct[] { return []; }
  saveProduct(): ProductionProduct { this.err('saveProduct'); }
  deleteProduct(): void { this.err('deleteProduct'); }
  getForecast(): ForecastLine[] { return []; }
  saveForecastLine(): ForecastLine { this.err('saveForecastLine'); }
  deleteForecastLine(): void { this.err('deleteForecastLine'); }
  clearForecast(): void { this.err('clearForecast'); }
}

// Provider registry. Keep instances around — they're stateless so a single
// instance per backend is fine.
const LOCAL_PROVIDER = new LocalStorageProviderImpl();
const ADVANTAGE_PROVIDER = new NotImplementedProvider('advantage', 'Advantage');
const DUCKDB_PROVIDER = new NotImplementedProvider('duckdb', 'DuckDB');

function providerFor(backend: ProductionStorageBackend): ProductionStorageProvider {
  switch (backend) {
    case 'local':     return LOCAL_PROVIDER;
    case 'advantage': return ADVANTAGE_PROVIDER;
    case 'duckdb':    return DUCKDB_PROVIDER;
  }
}

// ─── Active config (per tenant) ───────────────────────────────────────────

function configKey(slug: string): string { return `${LS_PREFIX}${slug}_storage_config`; }

export function getStorageConfig(slug: string): ProductionStorageConfig {
  if (!slug) return DEFAULT_STORAGE_CONFIG;
  try {
    const raw = localStorage.getItem(configKey(slug));
    if (!raw) return DEFAULT_STORAGE_CONFIG;
    const parsed = JSON.parse(raw) as Partial<ProductionStorageConfig>;
    return { ...DEFAULT_STORAGE_CONFIG, ...parsed };
  } catch {
    return DEFAULT_STORAGE_CONFIG;
  }
}

export function setStorageConfig(slug: string, config: ProductionStorageConfig): void {
  if (!slug) return;
  localStorage.setItem(configKey(slug), JSON.stringify(config));
}

// ─── Public surface (unchanged from Phase 1) ──────────────────────────────
//
// Pages call these freely; they delegate to the active provider per
// collection. Items / Recipes / Products / Forecast each pick their own
// backend independently.

export function getItems(slug: string): ProductionItem[] {
  return providerFor(getStorageConfig(slug).items).getItems(slug);
}

export function saveItem(slug: string, item: Partial<ProductionItem> & { name: string }): ProductionItem {
  return providerFor(getStorageConfig(slug).items).saveItem(slug, item);
}

export function deleteItem(slug: string, id: string): void {
  providerFor(getStorageConfig(slug).items).deleteItem(slug, id);
}

export function getRecipes(slug: string): ProductionRecipe[] {
  return providerFor(getStorageConfig(slug).recipes).getRecipes(slug);
}

export function saveRecipe(slug: string, recipe: Partial<ProductionRecipe> & { name: string }): ProductionRecipe {
  return providerFor(getStorageConfig(slug).recipes).saveRecipe(slug, recipe);
}

export function deleteRecipe(slug: string, id: string): void {
  providerFor(getStorageConfig(slug).recipes).deleteRecipe(slug, id);
}

export function getProducts(slug: string): ProductionProduct[] {
  return providerFor(getStorageConfig(slug).products).getProducts(slug);
}

export function saveProduct(
  slug: string,
  product: Partial<ProductionProduct> & { name: string; recipeId: string },
): ProductionProduct {
  return providerFor(getStorageConfig(slug).products).saveProduct(slug, product);
}

export function deleteProduct(slug: string, id: string): void {
  providerFor(getStorageConfig(slug).products).deleteProduct(slug, id);
}

// Forecast lives only locally — it's per-tenant per-user planning state,
// not source-of-truth data. Always uses LocalStorageProvider.

export function getForecast(slug: string): ForecastLine[] {
  return LOCAL_PROVIDER.getForecast(slug);
}

export function saveForecastLine(
  slug: string,
  line: Partial<ForecastLine> & { productId: string; qty: number },
): ForecastLine {
  return LOCAL_PROVIDER.saveForecastLine(slug, line);
}

export function deleteForecastLine(slug: string, id: string): void {
  LOCAL_PROVIDER.deleteForecastLine(slug, id);
}

export function clearForecast(slug: string): void {
  LOCAL_PROVIDER.clearForecast(slug);
}

// ─── Bulk admin (export/import/clear) ─────────────────────────────────────

export function exportAllData(slug: string): ProductionExport {
  return {
    version: 1,
    tenant: slug,
    exportedAt: nowIso(),
    items: getItems(slug),
    recipes: getRecipes(slug),
    products: getProducts(slug),
    forecast: getForecast(slug),
  };
}

export function importAllData(slug: string, data: Partial<ProductionExport>): void {
  // Import writes to local only — importing into a remote backend is a Phase 3 concern.
  if (Array.isArray(data.items))    lsWrite(slug, 'items', data.items);
  if (Array.isArray(data.recipes))  lsWrite(slug, 'recipes', data.recipes);
  if (Array.isArray(data.products)) lsWrite(slug, 'products', data.products);
  if (Array.isArray(data.forecast)) lsWrite(slug, 'forecast', data.forecast);
}

export function clearAllData(slug: string): void {
  if (!slug) return;
  for (const collection of ['items', 'recipes', 'products', 'forecast']) {
    localStorage.removeItem(lsKey(slug, collection));
  }
}

/**
 * Seed the tenant's local storage with the sample dataset (Items + Recipes
 * + Products). Always writes to `local` regardless of the active backend
 * config — seeding into a remote source-of-truth backend is intentional
 * out of scope. Existing collections are preserved unless `force` is true.
 */
export function loadSampleData(slug: string, force = false): { itemsAdded: number; recipesAdded: number; productsAdded: number } {
  const existingItems    = force ? [] : lsRead<ProductionItem>(slug, 'items');
  const existingRecipes  = force ? [] : lsRead<ProductionRecipe>(slug, 'recipes');
  const existingProducts = force ? [] : lsRead<ProductionProduct>(slug, 'products');
  const itemsToAdd    = SEED_ITEMS.filter((s) => !existingItems.some((x) => x.id === s.id));
  const recipesToAdd  = SEED_RECIPES.filter((s) => !existingRecipes.some((x) => x.id === s.id));
  const productsToAdd = SEED_PRODUCTS.filter((s) => !existingProducts.some((x) => x.id === s.id));
  lsWrite(slug, 'items',    [...existingItems, ...itemsToAdd]);
  lsWrite(slug, 'recipes',  [...existingRecipes, ...recipesToAdd]);
  lsWrite(slug, 'products', [...existingProducts, ...productsToAdd]);
  return {
    itemsAdded: itemsToAdd.length,
    recipesAdded: recipesToAdd.length,
    productsAdded: productsToAdd.length,
  };
}

// ─── Cost calculations ────────────────────────────────────────────────────

export function calcRecipeCost(
  recipe: ProductionRecipe,
  itemsById: Record<string, ProductionItem>,
): { totalCost: number; costPerUnit: number; hasMissing: boolean } {
  let total = 0;
  let hasMissing = false;
  for (const c of recipe.components) {
    const item = itemsById[c.itemId];
    if (!item) { hasMissing = true; continue; }
    total += (c.qty || 0) * (item.costPerUnit || 0);
  }
  const yieldQty = recipe.yieldQty || 1;
  return { totalCost: total, costPerUnit: total / yieldQty, hasMissing };
}

/**
 * Cost of one unit of a Product = (recipe cost per yield-unit × unitsPerBatch)
 * + sum(packaging line cost).
 */
export function calcProductCost(
  product: ProductionProduct,
  recipesById: Record<string, ProductionRecipe>,
  itemsById: Record<string, ProductionItem>,
): { totalCost: number; recipeCost: number; packagingCost: number; hasMissing: boolean } {
  const recipe = recipesById[product.recipeId];
  if (!recipe) {
    return { totalCost: 0, recipeCost: 0, packagingCost: 0, hasMissing: true };
  }
  const { costPerUnit, hasMissing: recipeMissing } = calcRecipeCost(recipe, itemsById);
  const recipeCost = costPerUnit * (product.unitsPerBatch || 1);
  let packagingCost = 0;
  let pkgMissing = false;
  for (const p of product.packaging) {
    const item = itemsById[p.itemId];
    if (!item) { pkgMissing = true; continue; }
    packagingCost += (p.qty || 0) * (item.costPerUnit || 0);
  }
  return {
    totalCost: recipeCost + packagingCost,
    recipeCost,
    packagingCost,
    hasMissing: recipeMissing || pkgMissing,
  };
}
